import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import TimeSlotAnalysisChart from '../../components/TimeSlotAnalysisChart';
import { to12h } from '../../utils/classroomUtilizationHelpers';

function formatHours(hours) {
  if (hours === null || hours === undefined) return '—';
  const hrs = Math.floor(hours);
  const mins = Math.round((hours - hrs) * 60);
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

export default function ClassroomUtilizationDetailView() {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const classroomId = searchParams.get('classroomId');
  const roomName = searchParams.get('name') || '';
  const roomFloor = searchParams.get('floor') || '';
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const dayType = searchParams.get('dayType') === 'weekend' ? 'weekend' : 'weekday';
  const operatingHoursPerDay = searchParams.get('operatingHoursPerDay') || '8.5';

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/classroom-utilization/detail', {
          params: { classroomId, startDate, endDate, dayType, operatingHoursPerDay },
        });
        if (!isMounted) return;
        setDetail(resp?.data || null);
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || 'Failed to load calculation details');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DashboardLayout title="Classroom Utilization — Calculation Detail">
      <div className="mx-auto max-w-4xl">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : window.close())}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>

        <div className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50 p-5">
          <h2 className="text-base font-bold text-gray-900">
            {roomName || `Classroom #${classroomId}`} <span className="font-normal text-gray-500">({roomFloor})</span>
          </h2>
          <p className="text-xs capitalize text-gray-600">
            {dayType} utilization calculation &middot; {startDate} to {endDate}
          </p>
        </div>

        {loading ? (
          <p className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
            Loading calculation details…
          </p>
        ) : error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-sm text-red-600">{error}</p>
        ) : detail ? (
          <>
            <div className="mb-5 space-y-2 rounded-lg border border-indigo-200 bg-indigo-50 p-4 font-mono text-xs text-indigo-900">
              <p>
                Available Hours = {detail.dayCount} days &times; {detail.operatingHoursPerDay}h/day
                = <strong>{detail.availableHours}h</strong>
              </p>
              <p>
                Class Hours = sum of merged class session hours per date = <strong>{detail.classHours}h</strong>
              </p>
              <p>
                Event Hours = sum of merged other-event hours per date (seminars, exams, etc.)
                = <strong>{detail.eventHours}h</strong>
              </p>
              <p>
                Booked Hours = Class Hours + Event Hours = {detail.classHours}h + {detail.eventHours}h
                = <strong>{detail.bookedHours}h</strong>
              </p>
              <p>
                Utilization % = {detail.bookedHours}h &divide; {detail.availableHours}h &times; 100
                = <strong>{detail.utilizationPct ?? '—'}%</strong>
              </p>
              {detail.blockedHours > 0 && (
                <>
                  <p>
                    Blocked Hours (non-class reservations) = <strong>{detail.blockedHours}h</strong>
                  </p>
                  <p>
                    Effective Available Hours = {detail.availableHours}h − {detail.blockedHours}h
                    = <strong>{detail.effectiveAvailableHours}h</strong>
                  </p>
                  <p>
                    Effective Utilization % = {detail.bookedHours}h &divide; {detail.effectiveAvailableHours}h &times; 100
                    = <strong>{detail.effectiveUtilizationPct ?? '—'}%</strong>
                  </p>
                </>
              )}
            </div>

            <h4 className="mb-2 text-sm font-semibold text-gray-800">
              Daily Breakdown ({detail.dailyBreakdown.length} day{detail.dailyBreakdown.length === 1 ? '' : 's'} with sessions)
            </h4>
            {detail.dailyBreakdown.length === 0 ? (
              <p className="mb-5 text-sm text-gray-500">No sessions were held in this room during the selected period.</p>
            ) : (
              <div className="mb-5 overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50 text-gray-500">
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Sessions</th>
                      <th className="px-3 py-2 text-center">Day Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {detail.dailyBreakdown.map((d) => (
                      <tr key={d.date}>
                        <td className="px-3 py-2 align-top font-medium text-gray-700">{d.date}</td>
                        <td className="px-3 py-2">
                          {d.sessions.map((s) => (
                            <div key={s.scheduleId} className="mb-1 last:mb-0">
                              <span
                                className={`mr-1 inline-block rounded px-1 py-0.5 text-[10px] font-bold uppercase ${
                                  s.type === 'event' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
                                }`}
                              >
                                {s.type === 'event' ? 'Event' : 'Class'}
                              </span>
                              <span className="font-mono text-gray-600">{to12h(s.startTime)} – {to12h(s.endTime)}</span>{' '}
                              <span className="font-semibold text-gray-700">({formatHours(s.durationHours)})</span>{' '}
                              <span className="text-gray-500">
                                {s.subjectShortName ? `· ${s.subjectShortName}` : ''}
                                {s.batchLabel ? ` · ${s.batchLabel}` : ''}
                              </span>
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2 text-center align-top">
                          <div className="font-semibold text-gray-700">{d.dayHours}h</div>
                          {d.dayEventHours > 0 && (
                            <div className="text-[10px] text-gray-400">Class {d.dayClassHours}h &middot; Event {d.dayEventHours}h</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200 bg-gray-50 font-semibold text-gray-800">
                      <td className="px-3 py-2" colSpan={2}>Total</td>
                      <td className="px-3 py-2 text-center">{detail.bookedHours}h</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {detail.blocks.length > 0 && (
              <>
                <h4 className="mb-2 text-sm font-semibold text-gray-800">Blocked (non-class) reservations</h4>
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50 text-gray-500">
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Time</th>
                        <th className="px-3 py-2">Title</th>
                        <th className="px-3 py-2 text-center">Hours</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detail.blocks.map((b, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">{b.date}</td>
                          <td className="px-3 py-2 font-mono text-gray-600">{to12h(b.startTime)} – {to12h(b.endTime)}</td>
                          <td className="px-3 py-2">{b.title || '—'}</td>
                          <td className="px-3 py-2 text-center">{b.durationHours}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <TimeSlotAnalysisChart
              analysis={detail.timeSlotAnalysis}
              occupiedLabel="days"
              description={`Hourly occupancy for this room — the % of ${detail.dayCount} ${dayType} days in range where any part of that hour was booked (a class or an event). Use this to spot peak hours to relieve and free ranges worth filling with new classes.`}
              freeRanges={detail.timeSlotAnalysis?.freeRanges}
              averageBusyWindow={detail.timeSlotAnalysis?.averageBusyWindow}
            />
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
